import { useState, useMemo } from 'react';
import {
  IconPlus, IconEdit, IconArrowLeft, IconWorldUpload,
  IconTrash, IconDeviceFloppy, IconFile, IconCategory,
} from '@tabler/icons-react';
import { useProducts } from '../context/ProductsContext';
import { useSiteContent, useCollectionPages } from '../context/SiteContentContext';
import { showToast } from '../components/ui/Toast';
import { getPageImage, setPageImage, removePageImage } from '../lib/imageStore';
import ImageUploader from '../components/ui/ImageUploader';
import { CATEGORIES } from '../lib/etsyImport';
import { ensureCategoryPages, pageProducts, categoryPagesText } from '../lib/categoryPages';

// Collection pages live in site_content.collection_pages (one array),
// so every change saves the whole list.

export function upsertPage(pages, page) {
  var index = page.id == null ? -1 : pages.findIndex(function(p) { return p.id === page.id; });
  if (index >= 0) {
    var next = pages.slice();
    next[index] = page;
    return { pages: next, page: page };
  }
  var maxId = pages.reduce(function(m, p) { return typeof p.id === 'number' && p.id > m ? p.id : m; }, 0);
  var added = Object.assign({}, page, { id: maxId + 1 });
  return { pages: pages.concat([added]), page: added };
}

// Every page needs its own id to be edited or deleted; a stored page
// without one (odd data) gets the next free number, saved on the next save.
export function withIds(pages) {
  var maxId = pages.reduce(function(m, p) { return typeof p.id === 'number' && p.id > m ? p.id : m; }, 0);
  var seen = {};
  return pages.map(function(p) {
    var ok = p.id != null && !seen[p.id];
    if (ok) { seen[p.id] = true; return p; }
    maxId += 1;
    return Object.assign({}, p, { id: maxId });
  });
}

export function slugify(text) {
  return String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function AdminPages() {
  var rawPages = useCollectionPages().pages;
  var pages = useMemo(function() { return withIds(rawPages); }, [rawPages]);
  var save = useSiteContent().save;
  var allProducts = useProducts().allProducts || [];
  var _editing = useState(null);
  var editing = _editing[0]; var setEditing = _editing[1];
  var [creating, setCreating] = useState(false);
  var [toggling, setToggling] = useState({});

  // Resolves true when saved, so the form knows whether to close.
  async function savePages(next, okMessage) {
    var r = await save({ collection_pages: next });
    if (r && r.error) { showToast(r.error, 'error'); return false; }
    showToast(okMessage);
    return true;
  }

  async function handleSave(page, productIdsChanged) {
    var current = page.id == null ? null : pages.find(function(p) { return p.id === page.id; });
    var clash = pages.find(function(p) { return p.slug === page.slug && p !== current; });
    if (clash) { showToast('Another page already uses the web address "' + page.slug + '". Change the page name.', 'error'); return; }
    var sameCategory = page.category && pages.find(function(p) { return p.category === page.category && p !== current; });
    if (sameCategory) { showToast('"' + sameCategory.name + '" already fills with ' + page.category + ' products. Pick another category, or none.', 'error'); return; }
    // Product ticks may have changed this page (from the product form)
    // since it was opened; keep those unless this form changed them.
    if (current && !productIdsChanged) page = Object.assign({}, page, { product_ids: current.product_ids || [] });
    var res = upsertPage(pages, page);
    if (await savePages(res.pages, page.status === 'live' ? 'Page published' : 'Page saved')) setEditing(null);
  }

  async function handleDelete(id) {
    var next = pages.filter(function(p) { return p.id !== id; });
    if (await savePages(next, 'Page deleted')) setEditing(null);
  }

  // "Show in shop" tick box: Live when ticked, Draft when not, saved at once.
  // Only that page's status changes; the rest of the list is saved as it is.
  async function toggleShown(page) {
    if (toggling[page.id]) return;
    var live = page.status !== 'live';
    var next = pages.map(function(p) { return p.id === page.id ? Object.assign({}, p, { status: live ? 'live' : 'draft' }) : p; });
    setToggling(function(t) { return Object.assign({}, t, { [page.id]: true }); });
    try {
      await savePages(next, page.name + (live ? ' is now showing in the shop' : ' is now hidden from the shop'));
    } finally {
      setToggling(function(t) { var c = Object.assign({}, t); delete c[page.id]; return c; });
    }
  }

  async function createFromCategories() {
    if (creating) return;
    var res = ensureCategoryPages(pages, CATEGORIES);
    if (!res.added.length && !res.linked.length) { showToast('All categories already have a page'); return; }
    setCreating(true);
    try { await savePages(res.pages, categoryPagesText(res)); } finally { setCreating(false); }
  }

  if (editing) {
    return <PageForm
      page={editing}
      pages={pages}
      onSave={handleSave}
      onBack={function() { setEditing(null); }}
      onDelete={handleDelete}
    />;
  }

  function newPage() {
    setEditing({
      id: null,
      name: '',
      slug: '',
      eyebrow: 'The collection',
      description: '',
      parent_nav: 'Shop',
      product_ids: [],
      sort_order: pages.length + 1,
      status: 'draft',
      category: null,
    });
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h1 className="adm-title">Pages</h1>
          <p className="adm-sub" style={{ marginBottom: 0 }}>Create pages to organise your products into categories. They appear as dropdown links under your nav.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="adm-btn adm-btn--outline" disabled={creating} onClick={createFromCategories}><IconCategory size={14} /> Create pages from categories</button>
          <button className="adm-btn" onClick={newPage}><IconPlus size={14} /> New page</button>
        </div>
      </div>

      {pages.length === 0 ? (
        <div className="adm-card" style={{ textAlign: 'center', padding: 40 }}>
          <IconFile size={28} color="#999" />
          <p style={{ fontSize: 13, color: '#888', marginTop: 12 }}>No pages yet. Click "Create pages from categories" for one page per kind of bag, or "New page" to make your own.</p>
          <p style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>For example: Briefcases, Totes, Wallets, Belts</p>
        </div>
      ) : (
        <div className="adm-card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'rgba(74,46,26,0.04)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#888' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}>Page name</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}>Linked to</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}>Products</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}>Show in shop</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}>Status</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}></th>
              </tr>
            </thead>
            <tbody>
              {pages.map(function(page) {
                var count = pageProducts(page, allProducts).length;
                return (
                  <tr key={page.id} onClick={function() { setEditing(Object.assign({}, page)); }} style={{ borderBottom: '1px solid rgba(74,46,26,0.08)', cursor: 'pointer' }}>
                    <td style={{ padding: '12px 14px', fontWeight: 500 }}>
                      {page.name}
                      {page.category && <div style={{ fontSize: 11, fontWeight: 400, color: '#888', marginTop: 2 }}>{'Fills with every live ' + page.category}</div>}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#888' }}>{page.parent_nav}</td>
                    <td style={{ padding: '12px 14px', color: '#888' }}>{count + ' product' + (count !== 1 ? 's' : '')}</td>
                    <td style={{ padding: '4px 8px' }} onClick={function(e) { e.stopPropagation(); }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 6px', cursor: toggling[page.id] ? 'wait' : 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={page.status === 'live'}
                          disabled={!!toggling[page.id]}
                          aria-label={'Show ' + page.name + ' in shop'}
                          onChange={function() { toggleShown(page); }}
                          style={{ width: 16, height: 16, accentColor: '#1E3A2F', cursor: 'inherit' }}
                        />
                      </label>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{
                        fontSize: 10, padding: '3px 9px', borderRadius: 20, letterSpacing: '0.07em',
                        background: page.status === 'live' ? '#E6F4EC' : '#f0f0f0',
                        color: page.status === 'live' ? '#1E6B3A' : '#888'
                      }}>{page.status === 'live' ? 'Live' : 'Draft'}</span>
                    </td>
                    <td style={{ padding: '12px 14px' }}><IconEdit size={15} color="#888" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PageForm(props) {
  var page = props.page;
  var onSave = props.onSave;
  var onBack = props.onBack;
  var onDelete = props.onDelete;
  var productsCtx = useProducts();
  var allProducts = productsCtx.allProducts;

  var otherPages = (props.pages || []).filter(function(p) { return p.id !== page.id; });
  var _form = useState(Object.assign({}, page, { product_ids: page.product_ids || [], category: page.category || null }));
  var form = _form[0]; var setForm = _form[1];
  var imgKey = 'collection_' + (form.id || 'new');
  var _heroImg = useState(getPageImage(imgKey));
  var heroImg = _heroImg[0]; var setHeroImg = _heroImg[1];

  function set(key, value) { setForm(Object.assign({}, form, { [key]: value })); }

  function toggleProduct(productId) {
    var ids = form.product_ids.slice();
    var index = ids.indexOf(productId);
    if (index >= 0) { ids.splice(index, 1); } else { ids.push(productId); }
    set('product_ids', ids);
  }

  var _saving = useState(false);
  var saving = _saving[0]; var setSaving = _saving[1];

  async function handleSave(status) {
    if (saving) return;
    var name = (form.name || '').trim();
    if (!name) { showToast('Give the page a name first.', 'error'); return; }
    var slug = form.slug || slugify(name);
    if (!slug) { showToast('The page name needs at least one letter or number.', 'error'); return; }
    setSaving(true);
    try {
      var startIds = (page.product_ids || []).slice().sort().join('|');
      var nowIds = form.product_ids.slice().sort().join('|');
      await onSave(Object.assign({}, form, { name: name, slug: slug, status: status || form.status }), startIds !== nowIds);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (saving) return;
    if (!window.confirm('Delete this page? Your products won’t be affected.')) return;
    setSaving(true);
    try { await onDelete(form.id); } finally { setSaving(false); }
  }

  var navOptions = ['Shop', 'About', 'Care guide', 'Contact'];

  return (
    <div>
      <button className="adm-btn adm-btn--outline" onClick={onBack} style={{ marginBottom: 16 }}>
        <IconArrowLeft size={14} /> Back to pages
      </button>
      <h1 className="adm-title">{form.id ? 'Edit: ' + form.name : 'New page'}</h1>
      <p className="adm-sub">Fill in the details below. Customers will see this page when they click the link in your navigation.</p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
        <div>
          <div className="adm-card">
            <div className="adm-card-title">Page details</div>
            <div className="adm-field">
              <label className="adm-label">Page name (e.g. Briefcases, Wallets, Belts)</label>
              <input className="adm-input" value={form.name} onChange={function(e) { set('name', e.target.value); }} placeholder="e.g. Leather Bags" />
            </div>
            <div className="adm-field">
              <label className="adm-label">Short description (shown at the top of the page)</label>
              <textarea className="adm-textarea" value={form.description} onChange={function(e) { set('description', e.target.value); }} placeholder="A sentence or two about this collection" />
            </div>
            <div className="adm-field">
              <label className="adm-label">Small text above the title (optional)</label>
              <input className="adm-input" value={form.eyebrow} onChange={function(e) { set('eyebrow', e.target.value); }} placeholder="e.g. The collection" />
            </div>
            <div className="adm-field">
              <label className="adm-label" htmlFor="page-category">Category</label>
              <select id="page-category" className="adm-select" value={form.category || ''} onChange={function(e) { set('category', e.target.value || null); }}>
                <option value="">None (only the products ticked below)</option>
                {CATEGORIES.map(function(c) {
                  var taken = otherPages.find(function(p) { return p.category === c; });
                  return <option key={c} value={c} disabled={!!taken}>{c + (taken ? ' (used by ' + taken.name + ')' : '')}</option>;
                })}
              </select>
              <p style={{ fontSize: 11, color: '#888', margin: '6px 0 0', lineHeight: 1.5 }}>
                {form.category
                  ? 'Fills automatically with every live ' + form.category + ' product. Tick others below to add them as extras.'
                  : 'Pick a category and this page fills itself with every live product of that kind.'}
              </p>
            </div>
          </div>

          <div className="adm-card">
            <div className="adm-card-title">{form.category ? 'Extra products for this page' : 'Choose products for this page'}</div>
            <p style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>
              {form.category
                ? 'Every live ' + form.category + ' product is already on this page. Tick any others you also want to show here.'
                : 'Tick the products you want to show on this page. Only published products will appear to customers.'}
            </p>
            {allProducts.map(function(product) {
              var isAuto = !!form.category && product.category === form.category;
              var isChecked = isAuto || form.product_ids.indexOf(product.id) >= 0;
              var isDraft = product.status === 'draft';
              return (
                <label key={product.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
                  borderBottom: '1px solid rgba(74,46,26,0.06)', cursor: isAuto ? 'default' : 'pointer',
                  opacity: isDraft ? 0.5 : 1,
                }}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isAuto}
                    aria-label={'Show ' + product.name + ' on this page'}
                    onChange={function() { toggleProduct(product.id); }}
                    style={{ width: 16, height: 16, accentColor: '#1E3A2F' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#1A1A1A' }}>{product.name}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>
                      {product.category + ' \u00b7 $' + product.base_price}
                      {isAuto ? ' \u00b7 included automatically' : ''}
                      {isDraft ? ' \u00b7 (Draft \u2014 won\u2019t show to customers)' : ''}
                    </div>
                  </div>
                </label>
              );
            })}
            {allProducts.length === 0 && <p style={{ fontSize: 12, color: '#888' }}>No products yet. Add products first, then come back here.</p>}
          </div>
        </div>

        <div>
          <div className="adm-card">
            <div className="adm-card-title">Where does this page appear?</div>
            <div className="adm-field">
              <label className="adm-label">Show under this nav link</label>
              <select className="adm-select" value={form.parent_nav} onChange={function(e) { set('parent_nav', e.target.value); }}>
                {navOptions.map(function(opt) { return <option key={opt} value={opt}>{opt}</option>; })}
              </select>
            </div>
            <p style={{ fontSize: 10, color: '#888', marginTop: 8, lineHeight: 1.5 }}>
              When a customer hovers over this link in the navigation bar, they will see a dropdown with this page listed.
            </p>
          </div>

          <div className="adm-card">
            <div className="adm-card-title">Page image (optional)</div>
            <ImageUploader
              image={heroImg}
              onUpload={function(data) { setPageImage(imgKey, data); setHeroImg(data); }}
              onRemove={function() { removePageImage(imgKey); setHeroImg(null); }}
              label="Banner image"
            />
            <p style={{ fontSize: 10, color: '#888', marginTop: 8, lineHeight: 1.5 }}>
              If you add an image, it shows as a banner at the top of the page. If not, just the title and description show.
            </p>
          </div>

          <div className="adm-card">
            <div className="adm-card-title">Display order</div>
            <div className="adm-field">
              <label className="adm-label">Position in dropdown (1 = first)</label>
              <input className="adm-input" type="number" value={form.sort_order} onChange={function(e) { set('sort_order', Number(e.target.value)); }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="adm-btn adm-btn--outline" disabled={saving} onClick={function() { handleSave('draft'); }} style={{ flex: 1, justifyContent: 'center' }}>
              <IconDeviceFloppy size={14} /> Save draft
            </button>
            <button className="adm-btn" disabled={saving} onClick={function() { handleSave('live'); }} style={{ flex: 1, justifyContent: 'center' }}>
              <IconWorldUpload size={14} /> Publish
            </button>
          </div>
          {form.id && (
            <button className="adm-btn adm-btn--danger" disabled={saving} onClick={handleDelete} style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>
              <IconTrash size={14} /> Delete page
            </button>
          )}
        </div>
      </div>
    </div>
  );
}